import React, { useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Plus, Edit2, Trash2, FileText, MessageSquare } from "lucide-react";
import { useAuth } from "../lib/auth";
import { api, formatErr } from "../lib/api";

function BlogForm({ initial, onSave, onCancel }) {
  const [f, setF] = useState(initial || { title: "", excerpt: "", content: "", cover_image: "", tags: "", published: true });
  const [saving, setSaving] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        title: f.title,
        excerpt: f.excerpt,
        content: f.content,
        cover_image: f.cover_image,
        tags: typeof f.tags === "string" ? f.tags.split(",").map((t) => t.trim()).filter(Boolean) : f.tags,
        published: !!f.published,
      };
      const res = initial?.id
        ? await api.put(`/admin/blogs/${initial.id}`, payload)
        : await api.post("/admin/blogs", payload);
      onSave(res.data);
      toast.success(initial?.id ? "Blog updated" : "Blog created");
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={save} className="space-y-4" data-testid="blog-form">
      <input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} placeholder="Title" required className="w-full border border-[#E5E0D8] rounded-lg px-4 py-2.5" data-testid="blog-form-title" />
      <input value={f.cover_image} onChange={(e) => setF({ ...f, cover_image: e.target.value })} placeholder="Cover image URL" className="w-full border border-[#E5E0D8] rounded-lg px-4 py-2.5" data-testid="blog-form-cover" />
      <input value={f.tags} onChange={(e) => setF({ ...f, tags: e.target.value })} placeholder="Tags (comma separated)" className="w-full border border-[#E5E0D8] rounded-lg px-4 py-2.5" data-testid="blog-form-tags" />
      <textarea value={f.excerpt} onChange={(e) => setF({ ...f, excerpt: e.target.value })} placeholder="Excerpt (under 400 chars)" rows={2} required className="w-full border border-[#E5E0D8] rounded-lg px-4 py-2.5 resize-none" data-testid="blog-form-excerpt" />
      <textarea value={f.content} onChange={(e) => setF({ ...f, content: e.target.value })} placeholder="Content (HTML allowed)" rows={10} required className="w-full border border-[#E5E0D8] rounded-lg px-4 py-2.5 font-mono text-sm" data-testid="blog-form-content" />
      <label className="flex items-center gap-2 text-sm">
        <input type="checkbox" checked={f.published} onChange={(e) => setF({ ...f, published: e.target.checked })} data-testid="blog-form-published" /> Published
      </label>
      <div className="flex gap-3">
        <button type="submit" disabled={saving} className="btn-primary" data-testid="blog-form-save">{saving ? "Saving..." : "Save"}</button>
        <button type="button" onClick={onCancel} className="btn-outline" data-testid="blog-form-cancel">Cancel</button>
      </div>
    </form>
  );
}

export default function AdminDashboard() {
  const { user, loading, logout } = useAuth();
  const [tab, setTab] = useState("blogs");
  const [blogs, setBlogs] = useState([]);
  const [enquiries, setEnquiries] = useState([]);
  const [editing, setEditing] = useState(null);
  const [creating, setCreating] = useState(false);

  const fetchBlogs = () => api.get("/blogs?include_unpublished=true").then((r) => setBlogs(r.data));
  const fetchEnquiries = () => api.get("/enquiries").then((r) => setEnquiries(r.data));

  useEffect(() => { if (user) { fetchBlogs(); fetchEnquiries(); } }, [user]);

  if (loading) return <div className="min-h-screen flex items-center justify-center text-[#888]">Loading...</div>;
  if (!user) return <Navigate to="/admin/login" replace />;

  const removeBlog = async (id) => {
    if (!confirm("Delete this blog post?")) return;
    try {
      await api.delete(`/admin/blogs/${id}`);
      toast.success("Deleted");
      fetchBlogs();
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail));
    }
  };

  return (
    <div className="min-h-screen bg-[#FCFAF8]" data-testid="admin-dashboard">
      <header className="bg-white border-b border-[#E5E0D8]">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-5 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/" className="font-display text-xl text-[#4B0082]">I'm Ube</Link>
            <span className="text-[#888] text-sm">/ Admin</span>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-[#666]" data-testid="admin-user-email">{user.email}</span>
            <button onClick={logout} className="text-sm text-[#4B0082] inline-flex items-center gap-1 hover:underline" data-testid="admin-logout">
              <LogOut size={14} /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 md:px-10 py-10">
        <div className="flex gap-2 mb-8 border-b border-[#E5E0D8]">
          <button onClick={() => setTab("blogs")} className={`px-5 py-3 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${tab === "blogs" ? "border-[#4B0082] text-[#4B0082]" : "border-transparent text-[#888]"}`} data-testid="tab-blogs">
            <FileText size={14} /> Blogs ({blogs.length})
          </button>
          <button onClick={() => setTab("enquiries")} className={`px-5 py-3 text-sm font-medium border-b-2 transition-colors flex items-center gap-2 ${tab === "enquiries" ? "border-[#4B0082] text-[#4B0082]" : "border-transparent text-[#888]"}`} data-testid="tab-enquiries">
            <MessageSquare size={14} /> Enquiries ({enquiries.length})
          </button>
        </div>

        {tab === "blogs" && (
          <div>
            <div className="flex justify-between items-center mb-6">
              <h2 className="font-display text-3xl text-[#1A1A1A]">Blog posts</h2>
              {!creating && !editing && (
                <button onClick={() => setCreating(true)} className="btn-primary" data-testid="new-blog-btn">
                  <Plus size={16} /> New Post
                </button>
              )}
            </div>
            {(creating || editing) && (
              <div className="bg-white border border-[#E5E0D8] rounded-2xl p-6 mb-6">
                <h3 className="font-display text-xl mb-4">{editing ? "Edit Post" : "New Post"}</h3>
                <BlogForm
                  initial={editing}
                  onSave={() => { setEditing(null); setCreating(false); fetchBlogs(); }}
                  onCancel={() => { setEditing(null); setCreating(false); }}
                />
              </div>
            )}
            <div className="space-y-3">
              {blogs.map((b) => (
                <div key={b.id} className="bg-white border border-[#E5E0D8] rounded-xl p-5 flex items-center gap-4" data-testid={`admin-blog-${b.id}`}>
                  {b.cover_image && <img src={b.cover_image} alt="" className="h-16 w-24 object-cover rounded-lg" />}
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-[#1A1A1A] truncate">{b.title}</div>
                    <div className="text-xs text-[#888]">/{b.slug} · {b.published ? "Published" : "Draft"}</div>
                  </div>
                  <button onClick={() => setEditing({ ...b, tags: (b.tags || []).join(", ") })} className="h-9 w-9 rounded-lg bg-[#4B0082]/10 text-[#4B0082] flex items-center justify-center hover:bg-[#4B0082]/20" data-testid={`edit-${b.id}`}>
                    <Edit2 size={14} />
                  </button>
                  <button onClick={() => removeBlog(b.id)} className="h-9 w-9 rounded-lg bg-red-50 text-red-600 flex items-center justify-center hover:bg-red-100" data-testid={`delete-${b.id}`}>
                    <Trash2 size={14} />
                  </button>
                </div>
              ))}
              {blogs.length === 0 && <p className="text-[#888] text-center py-12">No blogs yet.</p>}
            </div>
          </div>
        )}

        {tab === "enquiries" && (
          <div>
            <h2 className="font-display text-3xl text-[#1A1A1A] mb-6">Bulk Enquiries</h2>
            <div className="space-y-3">
              {enquiries.map((e) => (
                <div key={e.id} className="bg-white border border-[#E5E0D8] rounded-xl p-5" data-testid={`enquiry-${e.id}`}>
                  <div className="flex flex-wrap gap-4 items-baseline mb-2">
                    <div className="font-medium text-[#1A1A1A]">{e.name}</div>
                    <span className="text-xs px-2 py-0.5 bg-[#4B0082]/10 text-[#4B0082] rounded-full">{e.quantity}</span>
                    {e.interested_grade && (
                      <span className="text-xs px-2 py-0.5 bg-[#D4AF37]/20 text-[#B5952F] rounded-full font-medium" data-testid={`enquiry-grade-${e.id}`}>{e.interested_grade}</span>
                    )}
                    <span className="text-xs text-[#888]">{new Date(e.created_at).toLocaleString("en-IN")}</span>
                  </div>
                  <div className="text-sm text-[#555] grid sm:grid-cols-2 gap-1">
                    <div>📞 {e.contact}</div>
                    <div>✉️ {e.email}</div>
                    {e.company && <div>🏢 {e.company}</div>}
                  </div>
                  {e.message && <p className="text-sm text-[#444] mt-3 bg-[#F3EFEA] p-3 rounded-lg">{e.message}</p>}
                </div>
              ))}
              {enquiries.length === 0 && <p className="text-[#888] text-center py-12">No enquiries yet.</p>}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
